import { DOCUMENT, isPlatformBrowser, ViewportScroller } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, inject, PLATFORM_ID, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';

import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';
import { AnalyticsService } from '../../services/analytics.service';
import { PresaleDataService } from '../../services/connection-services/data.service';
import { ReownService } from '../../services/connection-services/reown.service';
import { EventService } from '../../services/event.service';
import { PixelService } from '../../services/pixel.service';
import { FRAGMENT_SCROLL_DELAY_MS } from '../../shared/constants/app-config.const';
import { CHAINS, getCookie, getReferrer, setCookie, verifyLanguage } from '../../shared/constants/app-enums';

@Component({
  selector: 'app-language',
  templateUrl: './language.component.html',
  styleUrls: ['./language.component.scss'],
  standalone: false,
})
export class LanguageComponent implements AfterViewInit {
  // readonly iframeRef = viewChild.required<ElementRef<HTMLIFrameElement>>('iframeRef');
  lang!: string;
  viewportScroller: ViewportScroller = inject(ViewportScroller);
  notificationUrl = environment.sseUrl;
  isProduction = environment.production;
  isLoaded: boolean = false;
  isDesclaimerClosed: boolean = false;
  notificationFadeOutTime = 50000;

  pixelConfig = {
    CONSENT_ENVIRONMENT: environment.pixelEnv,
    CONSENT_API_KEY: environment.crmPixelApiKey,
    PROJECT_IDENTIFIER: environment.slug,
    PROJECT_CHAIN: "EVM", // 'EVM', 'SOL' , 'BTC' and etc.
    PROJECT_CHAIN_ID: environment.eth.chainIdInt,
    PRIMARY_COLOR: `#000000`,
    UUID: '',
    PROJECT_NAME: 'Seerdex Presale',
    TOKEN_ISSUER: 'Seerdex Team',
  };
  pixelUrl!: SafeResourceUrl;

  private reachedThresholds: Record<number, boolean> = {
    25: false,
    50: false,
    75: false,
    100: false,
  };

  private readonly platformId = inject(PLATFORM_ID);
  // Use the DOCUMENT token rather than the global `document`/`window` for SSR safety
  private readonly document = inject(DOCUMENT);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly eventService = inject(EventService);
  private readonly presaleDataService = inject(PresaleDataService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly pixelService = inject(PixelService);
  private readonly reownService = inject(ReownService);

  constructor() {
    if (this.pixelConfig.CONSENT_API_KEY === '') {
      this.isDesclaimerClosed = true;
    }
    this.translate.setDefaultLang('en');
    this.pixelUrl = this.sanitizer.bypassSecurityTrustResourceUrl(environment.crmPixelUrl);
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd), takeUntilDestroyed()).subscribe((navEnd: NavigationEnd) => {
      let lang = navEnd.urlAfterRedirects.split('/')[1];
      // console.log({lang}, "---lang");
      lang = lang.split('#')[0];
      lang = lang.split('?')[0];

      if (lang && verifyLanguage(lang)) {
        this.lang = lang;
      } else {
        this.lang = 'en';
      }

      if (isPlatformBrowser(this.platformId) && this.lang === 'en' && navEnd.id === 1) {
        const browserLang = this.detectBrowserLanguage();
        if (browserLang && browserLang !== 'en') {
          this.router.navigate([`/${browserLang}`]);
          return;
        }
      }

      this.translate.use(this.lang);

      // Track page view for pixel events
      const fullUrl = navEnd.urlAfterRedirects.split('?')[0]; // Remove query params
      const hashIndex = fullUrl.indexOf('#');

      let pageName = 'home';

      // Check if there's a hash fragment (e.g., #about, #tokenomics)
      if (hashIndex !== -1) {
        const hashFragment = fullUrl.substring(hashIndex + 1);
        if (hashFragment) {
          pageName = hashFragment;
        }
      } else {
        // No hash fragment, extract from URL path
        const pagePath = fullUrl;
        // Remove leading/trailing slashes and split by '/'
        const pathParts = pagePath
          .replace(/^\/|\/$/g, '')
          .split('/')
          .filter((p: string) => p);

        // Filter out language codes to get the actual page path
        const pagePathWithoutLang = pathParts.filter((p: string) => !verifyLanguage(p));

        if (pagePathWithoutLang.length > 0) {
          // Join remaining parts with '/' for nested routes, or use the first part for simple routes
          pageName = pagePathWithoutLang.join('/');
        }
      }

      this.pixelService.trackPageView(pageName);
    });

    this.route.fragment.pipe(takeUntilDestroyed()).subscribe((fragment: string | null) => {
      if (fragment) {
        // Delay allows the page to finish rendering before attempting anchor scroll
        setTimeout(() => this.viewportScroller.scrollToAnchor(fragment as string), FRAGMENT_SCROLL_DELAY_MS);
      }
    });

    // Re-send consent payload whenever wallet connects/changes
    // this.eventService.getEvent('UPDATE_CONSENT').pipe(takeUntilDestroyed()).subscribe((data) => {
    //   this.submitDataToPixel(data);
    // });
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.isLoaded = true;
      let oldConsent = getCookie('consent_status');
      this.pixelService.consentStatus = oldConsent == 'true';
      let oldUUID = getCookie('consent_user_id');
      if (oldUUID === 'undefined' || oldUUID === 'null') {
        oldUUID = undefined;
      }
      this.pixelConfig.UUID = oldUUID || '';

      // Update PixelService with existing UUID
      if (oldUUID) {
        this.pixelService.pixelUUID = oldUUID;
      }
      if (oldConsent == 'true') this.isDesclaimerClosed = true;
      /*
            this.pixelService.initializeSessionId(); // Generate session ID now that UUID is set
            const iframeRef = this.iframeRef();
            if (iframeRef?.nativeElement) {
              const iframe = iframeRef.nativeElement;
              // Post config to iframe once it finishes loading
              iframe.onload = () => {
                // Use DOCUMENT.location rather than window.location for SSR safety
                iframe.contentWindow?.postMessage({ ...this.pixelConfig, PROJECT_SITE_URL: this.document.location?.origin, REFERRED_ID: getReferrer() }, '*');
              };
            }
            // Listen for postMessage events from the consent iframe via DOCUMENT.defaultView
            this.document.defaultView?.addEventListener('message', (event: MessageEvent) => {
              if (event.data?.type === 'CONSENT_CLOSED') {
                this.isDesclaimerClosed = true;
                setCookie('consent_status', event.data?.consent);
      
                // Update PixelService when consent closed and UUID set
                if (event.data?.uuid && event.data.uuid !== 'undefined' && event.data.uuid !== 'null') {
                  this.pixelService.pixelUUID = event.data.uuid;
                  setCookie('consent_user_id', event.data.uuid);
                  this.pixelService.initializeSessionId(); // Generate session ID now that UUID is set
                }
      
                this.pixelService.consentStatus = event.data?.consent == 'true';
                this.analyticsService.vwoData('cookieInteracted', { accepted: event.data?.consent }, true);
              } else if (event.data?.type === 'RESIZE') {
                const iframeRefValue = this.iframeRef();
                if (iframeRefValue?.nativeElement) {
                  iframeRefValue.nativeElement.style.height = `${event.data.height}px`;
                }
              } else if (event.data?.type === 'UUID_GENERATED') {
                if (event.data?.uuid && event.data.uuid !== 'undefined' && event.data.uuid !== 'null') {
                  setCookie('consent_user_id', event.data.uuid);
                  this.pixelService.pixelUUID = event.data.uuid;
                } else {
                  // UUID is missing or invalid — close disclaimer to avoid blocking UX
                  this.isDesclaimerClosed = true;
                }
              } else if (event.data?.type === 'UUID_GENERATION_FAILED') {
                // Consent iframe failed to generate UUID — close to avoid blocking UX
                this.isDesclaimerClosed = true;
              }
            });
            */
    }
  }

  submitDataToPixel(customPayload: unknown = null) {
    /*
    const iframeRef = this.iframeRef();
    if (iframeRef?.nativeElement && isPlatformBrowser(this.platformId)) {
      const defaultPayload: Record<string, unknown> = {
        consent: this.pixelService.consentStatus,
        projectIdentifier: environment.slug,
        walletAddress: this.reownService.evmWalletAddress,
        chainSymbol: 'EVM',
        chainId: [this.presaleDataService.activeChain === CHAINS.EVM ? this.presaleDataService.chainId : 50101], // 50101 for sol requested by CRM team
        // Use DOCUMENT.location rather than window.location for SSR safety
        site: this.document.location?.origin,
        actions: 'wallet-connected',
        walletName: this.presaleDataService.connectedWalletName,
        timestamp: Date.now(),
      };
      if (this.presaleDataService.activeChain === CHAINS.SOL) {
        defaultPayload['chainId'] = null;
      }

      const finalPayload = customPayload && typeof customPayload === 'object'
        ? { ...defaultPayload, ...(customPayload as Record<string, unknown>) }
        : defaultPayload;
      finalPayload['referredId'] = getReferrer();
      iframeRef?.nativeElement.contentWindow?.postMessage(
        {
          type: 'UPDATE_CONSENT',
          payload: finalPayload,
        },
        '*',
      );
    }*/
  }

  private detectBrowserLanguage(): string | null {
    const raw = ((navigator.language || (navigator as any).userLanguage) ?? 'en').toLowerCase();
    // Map all zh variants (zh-CN, zh-TW, zh-HK, etc.) to the supported zh-hans value
    const normalized = raw.startsWith('zh') ? 'zh-hans' : raw.split('-')[0];
    return verifyLanguage(normalized) ? normalized : null;
  }

  /** Track scroll depth milestones and report to analytics */
  @HostListener('window:scroll', [])
  onWindowScroll() {
    // Use DOCUMENT rather than direct window/document globals for SSR safety
    const scrollTop = this.document.defaultView?.scrollY ?? 0;
    const windowHeight = this.document.defaultView?.innerHeight ?? 0;
    const fullHeight = this.document.body.scrollHeight;

    const scrollPercent = ((scrollTop + windowHeight) / fullHeight) * 100;

    [25, 50, 75, 100].forEach((threshold) => {
      if (scrollPercent >= threshold && !this.reachedThresholds[threshold]) {
        this.analyticsService.vwoData('pageScrolled', { depth: `${threshold}%` });
        this.reachedThresholds[threshold] = true;
      }
    });
  }
}
